import React, { useState } from 'react';
import { Box, Button, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { AddIcon, ArrowBackIcon } from '@chakra-ui/icons';
import ReusableTable from 'components/ReusableTable';
import useDebounce from 'constants/helper/useDebounce';
import { useGeneralInformationList } from './hooks/useGeneralInformationList';
import { useQueryClient } from '@tanstack/react-query';
import { useGeneralInformationDelete } from './hooks/useGeneralInformationDelete';
import Swal from 'sweetalert2';
import VideoLinkForm from './videoLinkForm';

const GeneralInformation = () => {
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const debouncedSearch = useDebounce(searchString, 500);
  const [selectedId, setSelectedId] = useState(null);
  const queryClient = useQueryClient();

  const handleSearch = (value) => setSearchString(value);
  const { generalInformation, totalPages, isLoading } =
    useGeneralInformationList(page, debouncedSearch);

  const tableData = generalInformation?.map((item, index) => ({
    ...item,
    srNo: (page - 1) * 20 + index + 1,
  }));

  const { mutate: deleteLink } = useGeneralInformationDelete();
  const swalBackground = useColorModeValue('#ffffff', '#1A202C');
  const swalColor = useColorModeValue('#000000', '#ffffff');

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This link will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#319795',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      background: swalBackground,
      color: swalColor,
      borderRadius: '15px',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteLink(id, {
          onSuccess: () => {
            Swal.fire({
              title: 'Deleted!',
              text: 'Link has been deleted successfully.',
              icon: 'success',
              timer: 1500,
              showConfirmButton: false,
              background: swalBackground,
              color: swalColor,
            });

            queryClient.invalidateQueries(['generalInformationList']);
          },
        });
      }
    });
  };

  return (
    <Box p={4} mt={10}>
      {!showForm && (
        <ReusableTable
          data={tableData}
          columns={[
            { key: 'srNo', label: 'Sr.No', width: '80px', headerSize: '14px' },
            {
              key: 'name',
              label: 'Name',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'link',
              label: 'Link',
              width: '300px',
              headerSize: '14px',
            },
          ]}
          page={page}
          totalPages={totalPages}
          loading={isLoading}
          onPageChange={setPage}
          showTopAction={true}
          topAction={{
            title: 'General Information',
            label: 'Add Link',
            icon: <AddIcon />,
            onClick: () => setShowForm(true),
          }}
          showSearch={true}
          onSearch={handleSearch}
          showAddBtn={true}
          showViewToggle={false}
          onEdit={(id) => {
            setSelectedId(id);
            setShowForm(true);
          }}
          onDelete={(row) => handleDelete(row._id)}
        />
      )}

      {showForm && (
        <Box w="100%">
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            gap={4}
            mb={4}
            ml={3}
          >
            <Text fontSize="22px" fontWeight="bold">
              General Information
            </Text>
            <Button
              variant="ghost"
              leftIcon={<ArrowBackIcon />}
              onClick={() => {
                setShowForm(false);
                setSelectedId(null);
              }}
            >
              Back
            </Button>
          </Flex>

          <VideoLinkForm
            setSelectedId={setSelectedId}
            editId={selectedId}
            onSuccess={() => {
              queryClient.invalidateQueries(['customNotificationList']);
              setShowForm(false);
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default GeneralInformation;
